package org.moxultimate.topwithmox.components.cards

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.moxultimate.topwithmox.components.buttons.BuyInButton
import org.moxultimate.topwithmox.components.buttons.BuyInOption
import org.moxultimate.topwithmox.components.texts.MoxGradientText
import org.moxultimate.topwithmox.statics.ProductStatics
import org.moxultimate.topwithmox.theme.*

/**
 * ProductCard
 * White rounded card that displays a product image, metadata, pros/cons,
 * and a list of shop buy-in buttons. Width is controlled by the caller via [modifier].
 *
 * @param imageSrc URL or path of the product hero image.
 * @param brand Brand name shown in bold above the product name (e.g. "Logitech").
 * @param productName Full product name shown below the brand (e.g. "G Pro X Superlight").
 * @param description Short body text describing the product.
 * @param pros List of pro bullet-point strings. Pass empty list to hide the Pros column.
 * @param cons List of con bullet-point strings. Pass empty list to hide the Cons column.
 * @param buyInSubtitle Subtitle under the "Buy In" heading.
 * @param buyInOptions Ordered list of shops rendered as [BuyInButton] rows.
 * @param modifier Optional outer modifier (e.g. width, margin, alignment).
 */
@Composable
fun ProductCard(
    imageSrc: String,
    brand: String,
    productName: String,
    description: String,
    pros: List<String> = emptyList(),
    cons: List<String> = emptyList(),
    buyInSubtitle: String = ProductStatics.BUY_IN_SUBTITLE,
    buyInOptions: List<BuyInOption> = emptyList(),
    label: ProductLabelType? = null,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .backgroundColor(Colors.White)
            .borderRadius(16.px)
            .boxShadow(blurRadius = 20.px, color = Colors.LightGray)
            .overflow(Overflow.Hidden),
    ) {
        // Hero image with optional ranking label overlaid top-left
        Box(modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
            ProductHeroImage(imageSrc = imageSrc)
            label?.let {
                ProductLabel(
                    type = it,
                    modifier = Modifier
                        .position(Position.Absolute)
                        .top(12.px)
                        .left(12.px),
                )
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(all = 16.px),
        ) {
            ProductBrandBlock(brand = brand, productName = productName)

            SpanText(
                text = description,
                modifier = Modifier
                    .fillMaxWidth()
                    .color(DarkGrayColor)
                    .fontFamily(FontFamily)
                    .fontSize(13.px)
                    .fontWeight(FontWeight.Normal)
                    .margin(top = 8.px),
            )

            if (pros.isNotEmpty() || cons.isNotEmpty()) {
                ProsConsRow(
                    pros = pros,
                    cons = cons,
                    modifier = Modifier.margin(top = 14.px),
                )
            }

            if (buyInOptions.isNotEmpty()) {
                BuyInSection(
                    subtitle = buyInSubtitle,
                    options = buyInOptions,
                    modifier = Modifier.margin(top = 16.px),
                )
            }
        }
    }
}

/**
 * ProductHeroImage
 * Full-width product image with rounded top corners only and cover crop.
 *
 * @param imageSrc URL or path of the hero image.
 */
@Composable
private fun ProductHeroImage(imageSrc: String) {
    Image(
        src = imageSrc,
        description = ProductStatics.PRODUCT_IMAGE_ALT,
        modifier = Modifier
            .fillMaxWidth()
            .height(200.px)
            .styleModifier {
                property("object-fit", "cover")
                property("display", "block")
            },
    )
}

/**
 * ProductBrandBlock
 * Stacked brand name (bold) and product name (semi-bold) with tight spacing.
 *
 * @param brand Brand name (e.g. "Logitech").
 * @param productName Full product name (e.g. "G Pro X Superlight").
 */
@Composable
private fun ProductBrandBlock(brand: String, productName: String) {
    Column(modifier = Modifier
        .fillMaxWidth()
        .padding(bottom = 4.px)
    ) {

        MoxGradientText(
            text = brand.uppercase(),
            fontSize = 14,
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontWeight(FontWeight.Bold)
        )

        SpanText(
            text = productName,
            modifier = Modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .fontSize(16.px)
                .fontWeight(FontWeight.SemiBold),
        )
    }
}

/**
 * ProsConsRow
 * Two-column layout: green "Pros" list on the left, red "Cons" list on the right.
 * Each column only renders if its list is non-empty.
 *
 * @param pros List of pro strings.
 * @param cons List of con strings.
 * @param modifier Optional outer modifier.
 */
@Composable
private fun ProsConsRow(
    pros: List<String>,
    cons: List<String>,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.px),
    ) {
        if (pros.isNotEmpty()) {
            Column(
                modifier = Modifier.weight(1),
                verticalArrangement = Arrangement.spacedBy(4.px),
            ) {
                SpanText(
                    text = ProductStatics.PROS_LABEL,
                    modifier = Modifier
                        .color(ProsGreenColor)
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.Bold),
                )
                pros.forEach { proItem ->
                    SpanText(
                        text = proItem,
                        modifier = Modifier
                            .color(DarkGrayColor)
                            .fontFamily(FontFamily)
                            .fontSize(12.px)
                            .fontWeight(FontWeight.Normal),
                    )
                }
            }
        }

        if (cons.isNotEmpty()) {
            Column(
                modifier = Modifier.weight(1),
                verticalArrangement = Arrangement.spacedBy(4.px),
            ) {
                SpanText(
                    text = ProductStatics.CONS_LABEL,
                    modifier = Modifier
                        .color(ConsRedColor)
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.Bold),
                )
                cons.forEach { conItem ->
                    SpanText(
                        text = conItem,
                        modifier = Modifier
                            .color(DarkGrayColor)
                            .fontFamily(FontFamily)
                            .fontSize(12.px)
                            .fontWeight(FontWeight.Normal),
                    )
                }
            }
        }
    }
}

/**
 * BuyInSection
 * "Buy In" heading + subtitle + stacked [BuyInButton] rows for each shop option.
 *
 * @param subtitle Small description shown below the heading.
 * @param options Ordered list of [BuyInOption] entries.
 * @param modifier Optional outer modifier.
 */
@Composable
private fun BuyInSection(
    subtitle: String,
    options: List<BuyInOption>,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(bottom = 12.px),
    ) {
        SpanText(
            text = ProductStatics.BUY_IN_HEADING,
            modifier = Modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .fontSize(18.px)
                .fontWeight(FontWeight.Bold),
        )
        SpanText(
            text = subtitle,
            modifier = Modifier
                .color(DarkGrayColor)
                .fontFamily(FontFamily)
                .fontSize(12.px)
                .fontWeight(FontWeight.Normal),
        )
        Column(
            modifier = Modifier.fillMaxWidth().padding(top = 12.px),
            verticalArrangement = Arrangement.spacedBy(24.px),
        ) {
            options.forEach { option ->
                BuyInButton(option = option)
            }
        }
    }
}
